// Wersja Range bez Poppera - do użycia wewnątrz Collapse
// @ts-nocheck

import React, { FC, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Range as RangeInput } from 'react-range';

import { IProductsFilter } from 'api/types';
import { Input } from 'components/controls';

import styles from 'theme/components/controls/Range/RangeInline.module.scss';

interface IFilter {
  filter_id: string;
  filter_value: string;
  filter_type?: 'singlechoice' | 'multichoice' | string;
}

interface IProps {
  queryFilters: IFilter[];
  onChange: (filters: IFilter[]) => void;
  filter: IProductsFilter;
}

// Constants
const DEFAULT_MIN = 0;
const DEFAULT_MAX = 10000;

const RangeInline: FC<IProps> = ({ filter, queryFilters, onChange }) => {
  // Pobranie wartości min i max z filtra
  const minValue = filter.values?.[0]?.value ? parseFloat(filter.values[0].value) : DEFAULT_MIN;
  const maxValue = filter.values?.[1]?.value ? parseFloat(filter.values[1].value) : DEFAULT_MAX;
  
  // Upewniamy się, że min < max
  const safeMin = minValue < maxValue ? minValue : DEFAULT_MIN;
  const safeMax = maxValue > minValue ? maxValue : DEFAULT_MAX;
  
  // Obliczenie wartości początkowych z queryFilters lub wartościami z filtra
  const initialValues = useMemo<[number, number]>(() => {
    const rangeFilter = queryFilters.find((f) => f.filter_id === filter.id);
    if (rangeFilter) {
      const [minStr, maxStr] = rangeFilter.filter_value.split(';');
      const min = parseInt(minStr) || safeMin;
      const max = parseInt(maxStr) || safeMax;
      return [min, max];
    }
    return [safeMin, safeMax];
  }, [queryFilters, filter.id, safeMin, safeMax]);
  
  const [values, setValues] = useState<[number, number]>(initialValues);
  const prevSafeMinRef = useRef(safeMin);
  const prevSafeMaxRef = useRef(safeMax);

  // Synchronizacja wartości gdy zmieniają się queryFilters lub wartości filtra
  useEffect(() => {
    const rangeFilter = queryFilters.find((f) => f.filter_id === filter.id);

    if (rangeFilter) {
      const [minStr, maxStr] = rangeFilter.filter_value.split(';');
      const min = parseInt(minStr) || safeMin;
      const max = parseInt(maxStr) || safeMax;
      setValues([min, max]);
    } else {
      // Aktualizuj tylko jeśli safeMin lub safeMax się zmieniły (np. gdy filter.values się załadują)
      if (prevSafeMinRef.current !== safeMin || prevSafeMaxRef.current !== safeMax) {
        setValues([safeMin, safeMax]);
        prevSafeMinRef.current = safeMin;
        prevSafeMaxRef.current = safeMax;
      } else if (values[0] === DEFAULT_MIN && values[1] === DEFAULT_MAX) {
        // Jeśli wartości są jeszcze domyślne, ustaw wartości z filtra
        setValues([safeMin, safeMax]);
      }
    }
  }, [queryFilters, filter.id, safeMin, safeMax]);

  // Aktualizacja wartości - tylko lokalny state (filtrowanie dopiero po kliknięciu przycisku)
  const updateValues = useCallback((newValues: number[]) => {
    setValues(newValues);
    
    onChange([
      ...queryFilters.filter((f) => f.filter_id !== filter.id),
      {
        filter_id: filter.id,
        filter_value: `${newValues[0]};${newValues[1]}`,
        filter_type: filter.type
      }
    ]);
  }, [queryFilters, filter.id, filter.type, onChange]);

  // Walidacja i aktualizacja pojedynczej wartości
  const handleInputChange = useCallback((index: 0 | 1, value: string) => {
    const numValue = parseInt(value.replace(/\D/g, '') || '0');
    const [min, max] = values;
    
    if (index === 0) {
      if (numValue <= max && numValue >= safeMin) {
        updateValues([numValue, max]);
      }
    } else {
      if (numValue >= min && numValue <= safeMax) {
        updateValues([min, numValue]);
      }
    }
  }, [values, safeMin, safeMax, updateValues]);

  return (
    <div className={styles.content}>
      <div className={styles.rangeWrapper}>
        {safeMin < safeMax && (
          <RangeInput
            step={1}
            min={safeMin}
            max={safeMax}
            values={[Math.max(safeMin, Math.min(values[0], safeMax)), Math.min(safeMax, Math.max(values[1], safeMin))]}
            onChange={updateValues}
          renderTrack={({ props, children }: any) => {
            const range = safeMax - safeMin;
            const leftPercent = ((values[0] - safeMin) / range) * 100;
            const widthPercent = ((values[1] - values[0]) / range) * 100;
            
            return (
              <div
                {...props}
                className={styles.track}
                style={props.style}>
                <div className={styles.trackBackground} />
                <div 
                  className={styles.trackActive}
                  style={{
                    left: `${leftPercent}%`,
                    width: `${widthPercent}%`
                  }}
                />
                {children}
              </div>
            );
          }}
          renderThumb={({ props, isDragged }: any) => {
            return (
              <div
                {...props}
                className={`${styles.thumb} ${isDragged ? styles.thumbDragged : ''}`}
                style={props.style}>
                <div className={styles.thumbInner} />
              </div>
            );
          }}
          />
        )}
      </div>
      <div className={styles.inputWrapper}>
        <div className={styles.inputWithSuffix}>
          <Input
            value={values[0].toString()}
            onChange={(value) => handleInputChange(0, value || '')}
          />
          <span className={styles.suffix}>zł</span>
        </div>
        <div className={styles.inputDash} />
        <div className={styles.inputWithSuffix}>
          <Input
            value={values[1].toString()}
            onChange={(value) => handleInputChange(1, value || '')}
          />
          <span className={styles.suffix}>zł</span>
        </div>
      </div>
    </div>
  );
};

export default RangeInline;

